import { useEffect, useRef, useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  TextField, Grid2 as Grid, Alert, FormControlLabel, Switch, CircularProgress,
  MenuItem, Divider, Typography,
} from "@mui/material";
import { useForm, Controller, useWatch } from "react-hook-form";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  itemsApi, itemCategoriesApi, itemFormsApi,
  type ItemDto, type UomDto,
} from "@/api/endpoints";
import { errorMessage } from "@/api/client";
import { UomPicker } from "@/components/UomPicker";
import { useTaxRegime } from "@/shared/hooks/useTaxRegime";
import { ITEM_TYPES } from "@/shared/itemTypes";

interface ItemFormProps {
  open: boolean;
  onClose: () => void;
  item?: ItemDto | null;
}

type FormData = Omit<ItemDto, "id"> & { id?: string };

const empty: FormData = {
  sku: "", name: "", genericName: "",
  itemType: "Medicine", hsnCode: "",
  baseUomId: "", costingMethod: "WeightedAverage",
  reorderLevel: 0, reorderQty: 0, minOrderQty: 0, safetyStock: 0, leadTimeDays: 0,
  isBatchTracked: true, isExpiryTracked: true,
  form: "", packaging: "", strength: "", manufacturer: "", category: "",
  defaultMrp: undefined, gstRate: 12,
  isScheduleDrug: false, scheduleCategory: "",
};

const COSTING_METHODS = ["FEFO", "FIFO", "WeightedAverage"];
const SCHEDULES = ["H", "H1", "X", "G", "J", "K"];

export function ItemForm({ open, onClose, item }: ItemFormProps) {
  const qc = useQueryClient();
  const isEdit = !!item;
  const { taxLabel } = useTaxRegime();
  const { control, handleSubmit, reset, setValue, formState: { errors, isSubmitting } } =
    useForm<FormData>({ defaultValues: empty });

  const isSchedule = useWatch({ control, name: "isScheduleDrug" });
  const selectedType = useWatch({ control, name: "itemType" });
  const [selectedUom, setSelectedUom] = useState<UomDto | null>(null);

  // Reference-data dropdowns (managed under Catalog → Item Categories / Item Forms).
  // Scoped to the selected item type: the API returns rows for that type PLUS global ones.
  const categoriesQuery = useQuery({
    queryKey: ["item-categories", "options", selectedType],
    queryFn: () => itemCategoriesApi.list({ itemType: selectedType || undefined, pageSize: 200 }),
    enabled: open,
  });
  const formsQuery = useQuery({
    queryKey: ["item-forms", "options", selectedType],
    queryFn: () => itemFormsApi.list({ itemType: selectedType || undefined, pageSize: 200 }),
    enabled: open,
  });
  const categoryNames = categoriesQuery.data?.items.map(c => c.name) ?? [];
  const formNames = formsQuery.data?.items.map(f => f.name) ?? [];
  // Keep an item's existing value selectable even if it predates / doesn't match the master list.
  const withCurrent = (options: string[], current?: string) =>
    current && !options.includes(current) ? [current, ...options] : options;

  // When the user switches item type, clear Category/Form so a now-out-of-scope value isn't kept.
  // Skip the change triggered by loading an item into the form (reset).
  const prevTypeRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!open) { prevTypeRef.current = undefined; return; }
    if (prevTypeRef.current !== undefined && prevTypeRef.current !== selectedType) {
      setValue("category", "");
      setValue("form", "");
    }
    prevTypeRef.current = selectedType;
  }, [selectedType, open, setValue]);

  useEffect(() => {
    if (!open) return;
    setSelectedUom(null); // picker will resolve from initialId on next render
    reset(item ? {
      ...item,
      genericName: item.genericName ?? "",
      hsnCode: item.hsnCode ?? "",
      form: item.form ?? "",
      packaging: item.packaging ?? "",
      strength: item.strength ?? "",
      manufacturer: item.manufacturer ?? "",
      category: item.category ?? "",
      scheduleCategory: item.scheduleCategory ?? "",
    } : empty);
    // Sync the type baseline so the clear-on-type-change effect doesn't fire on load.
    prevTypeRef.current = item ? item.itemType : empty.itemType;
  }, [item, open, reset]);

  const mutation = useMutation({
    meta: { successMessage: isEdit ? "Item updated" : "Item created" },
    mutationFn: async (data: FormData) => {
      const payload: Partial<ItemDto> = {
        ...data,
        genericName: data.genericName || undefined,
        hsnCode: data.hsnCode || undefined,
        form: data.form || undefined,
        packaging: data.packaging || undefined,
        strength: data.strength || undefined,
        manufacturer: data.manufacturer || undefined,
        category: data.category || undefined,
        scheduleCategory: data.isScheduleDrug ? (data.scheduleCategory || undefined) : undefined,
        defaultMrp: data.defaultMrp ?? undefined,
      };
      if (isEdit && item) {
        await itemsApi.update(item.id, payload);
        return item.id;
      }
      const result = await itemsApi.create(payload);
      return result.id;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["items"] });
      onClose();
    },
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <form onSubmit={handleSubmit((d) => mutation.mutate(d))}>
        <DialogTitle>{isEdit ? "Edit item" : "New item"}</DialogTitle>
        <DialogContent dividers>
          {mutation.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(mutation.error)}</Alert>}

          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Basics</Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Controller name="sku" control={control} rules={{ required: "Required", maxLength: 50 }}
                render={({ field }) => (
                  <TextField {...field} label="SKU *" fullWidth size="small" disabled={isEdit}
                    error={!!errors.sku} helperText={errors.sku?.message} />
                )} />
            </Grid>
            <Grid size={{ xs: 12, sm: 8 }}>
              <Controller name="name" control={control} rules={{ required: "Required", maxLength: 500 }}
                render={({ field }) => (
                  <TextField {...field} label="Name *" fullWidth size="small"
                    error={!!errors.name} helperText={errors.name?.message} />
                )} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Controller name="genericName" control={control}
                render={({ field }) => <TextField {...field} label="Generic name" fullWidth size="small" />}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <Controller name="itemType" control={control} rules={{ required: true }}
                render={({ field }) => (
                  <TextField {...field} select label="Type *" fullWidth size="small">
                    {ITEM_TYPES.map(t => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
                  </TextField>
                )} />
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <Controller name="hsnCode" control={control}
                render={({ field }) => <TextField {...field} label="HSN code" fullWidth size="small" />}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Controller name="form" control={control}
                render={({ field }) => (
                  <TextField {...field} select label="Form" fullWidth size="small"
                    helperText={formsQuery.isError ? "Could not load forms" : undefined}>
                    <MenuItem value=""><em>None</em></MenuItem>
                    {withCurrent(formNames, field.value).map(f => (
                      <MenuItem key={f} value={f}>{f}</MenuItem>
                    ))}
                  </TextField>
                )} />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Controller name="strength" control={control}
                render={({ field }) => <TextField {...field} label="Strength (e.g. 500mg)" fullWidth size="small" />}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Controller name="packaging" control={control}
                render={({ field }) => <TextField {...field} label="Packaging" fullWidth size="small" />}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Controller name="manufacturer" control={control}
                render={({ field }) => <TextField {...field} label="Manufacturer" fullWidth size="small" />}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Controller name="category" control={control}
                render={({ field }) => (
                  <TextField {...field} select label="Category" fullWidth size="small"
                    helperText={categoriesQuery.isError ? "Could not load categories" : undefined}>
                    <MenuItem value=""><em>None</em></MenuItem>
                    {withCurrent(categoryNames, field.value).map(c => (
                      <MenuItem key={c} value={c}>{c}</MenuItem>
                    ))}
                  </TextField>
                )} />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Stock & costing</Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Controller name="baseUomId" control={control} rules={{ required: "Required" }}
                render={({ field, fieldState }) => (
                  <UomPicker
                    value={selectedUom}
                    initialId={field.value || undefined}
                    onChange={(v) => { setSelectedUom(v); field.onChange(v?.id ?? ""); }}
                    required
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                  />
                )} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Controller name="costingMethod" control={control}
                render={({ field }) => (
                  <TextField {...field} select label="Costing method" fullWidth size="small">
                    {COSTING_METHODS.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                  </TextField>
                )} />
            </Grid>
            {(["reorderLevel", "reorderQty", "minOrderQty", "safetyStock"] as const).map(k => (
              <Grid key={k} size={{ xs: 6, sm: 3 }}>
                <Controller name={k} control={control}
                  rules={{ min: { value: 0, message: ">= 0" } }}
                  render={({ field }) => (
                    <TextField {...field} type="number" fullWidth size="small"
                      label={k.replace(/([A-Z])/g, " $1").replace(/^./, s => s.toUpperCase())}
                      onChange={(e) => field.onChange(e.target.value === "" ? 0 : Number(e.target.value))}
                      error={!!errors[k]} helperText={errors[k]?.message} />
                  )} />
              </Grid>
            ))}
            <Grid size={{ xs: 6, sm: 3 }}>
              <Controller name="leadTimeDays" control={control}
                rules={{ min: { value: 0, message: ">= 0" } }}
                render={({ field }) => (
                  <TextField {...field} type="number" label="Lead time (days)" fullWidth size="small"
                    onChange={(e) => field.onChange(e.target.value === "" ? 0 : Number(e.target.value))} />
                )} />
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
              <Controller name="isBatchTracked" control={control}
                render={({ field }) => (
                  <FormControlLabel label="Batch tracked"
                    control={<Switch checked={field.value} onChange={(_, v) => field.onChange(v)} />}
                  />
                )} />
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
              <Controller name="isExpiryTracked" control={control}
                render={({ field }) => (
                  <FormControlLabel label="Expiry tracked"
                    control={<Switch checked={field.value} onChange={(_, v) => field.onChange(v)} />}
                  />
                )} />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Pricing & compliance</Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Controller name="defaultMrp" control={control}
                render={({ field }) => (
                  <TextField {...field} type="number" label="Default MRP (₹)" fullWidth size="small"
                    onChange={(e) => field.onChange(e.target.value === "" ? undefined : Number(e.target.value))}
                    value={field.value ?? ""} />
                )} />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Controller name="gstRate" control={control}
                rules={{ min: { value: 0, message: "0-100" }, max: { value: 100, message: "0-100" } }}
                render={({ field }) => (
                  <TextField {...field} type="number" label={`${taxLabel} rate (%)`} fullWidth size="small"
                    onChange={(e) => field.onChange(e.target.value === "" ? 0 : Number(e.target.value))}
                    error={!!errors.gstRate} helperText={errors.gstRate?.message} />
                )} />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Controller name="isScheduleDrug" control={control}
                render={({ field }) => (
                  <FormControlLabel label="Schedule drug"
                    control={<Switch checked={field.value} onChange={(_, v) => field.onChange(v)} />}
                  />
                )} />
            </Grid>
            {isSchedule && (
              <Grid size={{ xs: 12, sm: 4 }}>
                <Controller name="scheduleCategory" control={control}
                  rules={{ required: isSchedule ? "Required when schedule drug" : false }}
                  render={({ field }) => (
                    <TextField {...field} select label="Schedule category *" fullWidth size="small"
                      error={!!errors.scheduleCategory} helperText={errors.scheduleCategory?.message}>
                      {SCHEDULES.map(s => <MenuItem key={s} value={s}>Schedule {s}</MenuItem>)}
                    </TextField>
                  )} />
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={isSubmitting}>Cancel</Button>
          <Button type="submit" variant="contained"
            disabled={isSubmitting || mutation.isPending}
            startIcon={mutation.isPending ? <CircularProgress size={16} color="inherit" /> : null}>
            {isEdit ? "Save changes" : "Create"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
